import React, { useState, useEffect, useRef } from 'react';
import type { FloorFixture } from '../../types';
import PlusIcon from '../icons/PlusIcon';
import TrashIcon from '../icons/TrashIcon';

interface EnhancedLayoutEditorProps {
  components: FloorFixture[];
  selectedComponentId: string | null;
  onSelectComponent: (id: string | null) => void;
  onChange: (id: string, updates: Partial<FloorFixture>) => void;
  onAdd: (type: FloorFixture['type']) => void;
  onDelete: (id: string) => void;
  onPositionChange: (id: string, x: number, y: number) => void;
  onGridSettingsChange?: (settings: { showGrid: boolean; snapToGrid: boolean; gridSize: number }) => void;
}

const componentTypeOptions: { value: FloorFixture['type']; label: string; icon: string; defaultSize: { width: number; height: number } }[] = [
  { value: 'entrance', label: 'Entrance', icon: '🚪', defaultSize: { width: 30, height: 5 } },
  { value: 'bar', label: 'Bar', icon: '🍺', defaultSize: { width: 8, height: 60 } },
  { value: 'patio', label: 'Patio', icon: '🌳', defaultSize: { width: 30, height: 25 } },
  { value: 'kitchen', label: 'Kitchen', icon: '👨‍🍳', defaultSize: { width: 20, height: 20 } },
  { value: 'restroom', label: 'Restroom', icon: '🚻', defaultSize: { width: 10, height: 10 } },
  { value: 'storage', label: 'Storage', icon: '📦', defaultSize: { width: 15, height: 15 } },
  { value: 'counter', label: 'Counter', icon: '📋', defaultSize: { width: 25, height: 8 } },
  { value: 'window', label: 'Window', icon: '🪟', defaultSize: { width: 20, height: 5 } },
  { value: 'wall', label: 'Wall', icon: '🧱', defaultSize: { width: 5, height: 100 } },
  { value: 'custom', label: 'Custom', icon: '⚙️', defaultSize: { width: 15, height: 15 } },
];

const EnhancedLayoutEditor: React.FC<EnhancedLayoutEditorProps> = ({
  components,
  selectedComponentId,
  onSelectComponent,
  onChange,
  onAdd,
  onDelete,
  onPositionChange,
  onGridSettingsChange,
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [gridSnap, setGridSnap] = useState(true);
  const [gridSize, setGridSize] = useState(5);
  const [showGrid, setShowGrid] = useState(true);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Notify parent of grid settings changes
  useEffect(() => {
    if (onGridSettingsChange) {
      onGridSettingsChange({ showGrid, snapToGrid: gridSnap, gridSize });
    }
  }, [showGrid, gridSnap, gridSize, onGridSettingsChange]);

  const selectedComponent = selectedComponentId 
    ? components.find(c => c.id === selectedComponentId)
    : null;

  const snapToGrid = (value: number): number => {
    if (!gridSnap) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  const handleAddComponent = (type: FloorFixture['type']) => {
    onAdd(type);
    setShowAddMenu(false);
  };

  const handleDeleteComponent = (id: string) => {
    if (window.confirm('Are you sure you want to delete this component?')) {
      onDelete(id);
      if (selectedComponentId === id) {
        onSelectComponent(null);
      }
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar - Using CSS Grid for explicit layout control */}
      <div ref={sidebarRef} className="w-80 bg-white border-r border-gray-200 h-full grid overflow-hidden" style={{ gridTemplateRows: selectedComponent ? 'auto 1fr auto' : 'auto 1fr' }}>
        {/* Header Section - Fixed at top */}
        <div className="p-4 border-b border-gray-200 bg-white overflow-visible">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Layout Components</h2>
          
          {/* Grid Controls */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Show Grid</label>
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                className="w-4 h-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Snap to Grid</label>
              <input
                type="checkbox"
                checked={gridSnap}
                onChange={(e) => setGridSnap(e.target.checked)}
                className="w-4 h-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
            </div>
            {gridSnap && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Grid Size: {gridSize}%</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={gridSize}
                  onChange={(e) => setGridSize(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* Add Component Button */}
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="w-full bg-pink-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors flex items-center justify-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Component
            </button>
            {showAddMenu && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                {componentTypeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleAddComponent(option.value)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                  >
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Components List - Scrollable middle section with explicit height */}
        <div className="overflow-y-auto overflow-x-hidden" style={{ minHeight: 0 }}>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 sticky top-0 bg-white pb-2 z-10 border-b border-gray-200 -mx-4 px-4">Existing Components ⚙️</h3>
            {components.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No components added yet. Click "Add Component" to get started.</p>
            ) : (
              <div className="space-y-2">
                {components.map(component => {
                  const typeOption = componentTypeOptions.find(opt => opt.value === component.type);
                  return (
                    <div
                      key={component.id}
                      onClick={() => onSelectComponent(component.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedComponentId === component.id
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{typeOption?.icon || '⚙️'}</span>
                          <span className="font-medium text-sm text-gray-800">{component.label}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteComponent(component.id);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete component"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Position: ({component.x.toFixed(1)}%, {component.y.toFixed(1)}%)</div>
                        <div>Size: {component.width}% × {component.height}%</div>
                        {component.rotation && component.rotation !== 0 && (
                          <div>Rotation: {component.rotation}°</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel - Fixed at bottom, scrollable if needed */}
        {selectedComponent && (
          <div className="border-t border-gray-200 bg-gray-50 overflow-y-auto" style={{ maxHeight: '300px' }}>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Properties</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                  <input
                    type="text"
                    value={selectedComponent.label}
                    onChange={(e) => onChange(selectedComponent.id, { label: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select
                    value={selectedComponent.type}
                    onChange={(e) => onChange(selectedComponent.id, { type: e.target.value as FloorFixture['type'] })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  >
                    {componentTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">X Position (%)</label>
                    <input
                      type="number"
                      step={gridSnap ? gridSize : 0.1}
                      value={selectedComponent.x}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        const snapped = snapToGrid(value);
                        onChange(selectedComponent.id, { x: Math.max(0, Math.min(100, snapped)) });
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Y Position (%)</label>
                    <input
                      type="number"
                      step={gridSnap ? gridSize : 0.1}
                      value={selectedComponent.y}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        const snapped = snapToGrid(value);
                        onChange(selectedComponent.id, { y: Math.max(0, Math.min(100, snapped)) });
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Width (%)</label>
                    <input
                      type="number"
                      step={gridSnap ? gridSize : 0.1}
                      min={1}
                      max={100}
                      value={selectedComponent.width}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1;
                        const snapped = snapToGrid(value);
                        onChange(selectedComponent.id, { width: Math.max(1, Math.min(100, snapped)) });
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Height (%)</label>
                    <input
                      type="number"
                      step={gridSnap ? gridSize : 0.1}
                      min={1}
                      max={100}
                      value={selectedComponent.height}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1;
                        const snapped = snapToGrid(value);
                        onChange(selectedComponent.id, { height: Math.max(1, Math.min(100, snapped)) });
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Rotation: {selectedComponent.rotation || 0}°
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="15"
                    value={selectedComponent.rotation || 0}
                    onChange={(e) => onChange(selectedComponent.id, { rotation: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0°</span>
                    <span>90°</span>
                    <span>180°</span>
                    <span>270°</span>
                    <span>360°</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedLayoutEditor;
